import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  DefaultValues,
  FieldValues,
  Path,
  Resolver,
  useForm,
} from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { z } from "zod";

import { useDisclosure } from "@heroui/react";

import {
  extractUnderlyingSchema,
  formatDateValueToISOString,
  generateDefaultValues,
  getChangedFields,
} from "@/features/FormCard/utils";

import { TTranslation, useFetchItem } from "@/features/FormCard/hooks";

import { deleteAuthorized, postAuthorized, putAuthorized } from "@/lib/fetch";
import { AxiosError } from "axios";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";

export type TEditFormParams = {
  client: string;
  table_name: string;
  FormSchema: z.ZodSchema<any>;
  adjustData?: (data: any) => any;
  language?: string;
  fieldsNotApplyTranslation?: ReadonlyArray<string>;
  disable_translations_request?: boolean;
  shouldUnregister?: boolean;
  adjustOnReset?: (
    key: string,
    value: any,
    fetchedData: any,
    translations?: TTranslation,
  ) => any;
} & (
  | {
      id: number | string;
    }
  | {
      isNew: true;
    }
);

const messages: Record<string, string> = {
  duplicate: "Cet élément existe déjà.",
  foreign_key: "Référence invalide.",
  not_null: "Un champ obligatoire est manquant.",
  data_error: "Données invalides.",
  db_unavailable: "Base de données indisponible.",
  integrity_error: "Contrainte d’intégrité.",
  unauthorized: "Non autorisé.",
  unexpected: "Erreur inconnue.",
};

type ApiErrorCode =
  | "duplicate"
  | "foreign_key"
  | "not_null"
  | "data_error"
  | "db_unavailable"
  | "integrity_error"
  | "unauthorized"
  | "unexpected";

function getApiErrorMessage(err: unknown): string | null {
  const anyErr = err as any;
  const responseData = anyErr?.response?.data;
  const detail = responseData?.detail;

  if (typeof detail === "string" && detail.trim()) {
    return detail;
  }

  if (detail && typeof detail === "object") {
    const message = (detail as Record<string, unknown>).message;
    if (typeof message === "string" && message.trim()) {
      return message;
    }
  }

  if (
    responseData &&
    typeof responseData === "object" &&
    typeof responseData.message === "string" &&
    responseData.message.trim()
  ) {
    return responseData.message;
  }

  return null;
}

function getApiErrorCode(err: unknown): ApiErrorCode {
  const anyErr = err as any;
  const detail = anyErr?.response?.data?.detail;
  const code =
    detail && typeof detail === "object"
      ? (detail as Record<string, unknown>).code
      : null;
  if (typeof code !== "string") return "unexpected";

  const allowed: ApiErrorCode[] = [
    "duplicate",
    "foreign_key",
    "not_null",
    "data_error",
    "db_unavailable",
    "integrity_error",
    "unauthorized",
    "unexpected",
  ];

  return (
    allowed.includes(code as ApiErrorCode) ? code : "unexpected"
  ) as ApiErrorCode;
}

function toastFromError(
  err: unknown,
  baseMessage:
    | string
    | "form.error.base_update"
    | "form.error.base_create"
    | "form.error.base_delete",
  t?: (key: string, opts?: any) => string,
) {
  const errorMessage = getApiErrorMessage(err);
  if (errorMessage) {
    if (t && baseMessage.startsWith("form.")) {
      toast.error(`${t(baseMessage)}: ${errorMessage}`);
      return;
    }

    const base_message = typeof baseMessage === "string" ? baseMessage : "";
    toast.error(`${base_message ? `${base_message}: ` : ""}${errorMessage}`);
    return;
  }

  const code = getApiErrorCode(err);

  if (t && baseMessage.startsWith("form.")) {
    const base = t(baseMessage);
    const detail = t(`form.error.${code}`);
    toast.error(`${base}: ${detail}`);
    return;
  }

  // Fallback: keep original behavior with FR messages
  const base_message = typeof baseMessage === "string" ? baseMessage : "";
  toast.error(
    `${base_message ? base_message + ":" : ""} ${messages[code] ?? messages.unexpected}`,
  );
}

/**
 * API function to update an existing item in the database.
 */
async function updateItem<ItemType>(
  id: number | string,
  changes: Partial<ItemType>,
  table_name: string,
  client: string,
): Promise<ItemType> {
  const response = await putAuthorized(
    `${import.meta.env.VITE_BACKEND_URL}/${client}/${table_name}/${id}`,
    changes,
  );

  return response.data;
}

/**
 * Custom mutation hook for updating items in the database.
 */
function useItemUpdate<ItemType>(
  table_name: string,
  queryClient: any,
  client: string,
  onClose: () => void,
  m_id: number | string,
  t?: (key: string, opts?: any) => string,
) {
  return useMutation({
    mutationFn: ({
      id,
      changes,
    }: {
      id: number | string;
      changes: Partial<ItemType>;
    }) => updateItem(id, changes, table_name, client),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [client, table_name] });
      queryClient.invalidateQueries({
        queryKey: [client, "audits", "get", { audit_id: m_id }],
      });

      // to revalidate also hold audit table (a7)
      queryClient.invalidateQueries({
        queryKey: [client, `${table_name}/audit`, "get", { audit_id: m_id }],
      });

      toast.success(
        t ? t("form.toast.update_success") : "Item successfully updated!",
      );
      onClose();
    },
    onError: (error: AxiosError) => {
      console.error("Error updating item:", error);
      toastFromError(error, "form.error.base_update", t);
    },
  });
}

/**
 * API function to create a new item in the database.
 */
async function createItem<ItemType>(
  data: ItemType,
  table_name: string,
  client: string,
): Promise<ItemType> {
  const response = await postAuthorized(
    `${import.meta.env.VITE_BACKEND_URL}/${client}/${table_name}`,
    data,
  );
  return response.data;
}

/**
 * Custom mutation hook for creating new items in the database.
 */
function useItemCreate<ItemType>(
  table_name: string,
  queryClient: any,
  client: string,
  onClose: () => void,
  t?: (key: string, opts?: any) => string,
) {
  return useMutation({
    mutationFn: (data: ItemType) => createItem(data, table_name, client),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [table_name] });
      toast.success(
        t ? t("form.toast.create_success") : "Item successfully created!",
      );
      onClose();
    },
    onError: (error: Error) => {
      console.error("Error creating item:", error);
      toastFromError(error, "form.error.base_create", t);
    },
  });
}

/**
 * API function to delete an item from the database.
 */
async function deleteItem(
  id: number | string,
  table_name: string,
  client: string,
): Promise<{ success: boolean; id: number | string }> {
  const response = await deleteAuthorized(
    `${import.meta.env.VITE_BACKEND_URL}/${client}/${table_name}/${id}`,
  );
  return response.data;
}

/**
 * Custom mutation hook for deleting items from the database.
 */
function useItemDelete(
  table_name: string,
  queryClient: any,
  client: string,
  onClose: () => void,
  t?: (key: string, opts?: any) => string,
  onSuccess?: () => void,
) {
  return useMutation({
    mutationFn: (id: number | string) => deleteItem(id, table_name, client),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [client, table_name] });
      if (!onSuccess) {
        toast.success(
          t ? t("form.toast.delete_success") : "Élément supprimé avec succès !",
        );
      }
      onClose();
      if (onSuccess) {
        onSuccess();
      }
    },
    onError: (error: Error) => {
      console.error("Erreur lors de la suppression :", error);
      toastFromError(error, "form.error.base_delete", t);
    },
  });
}

/**
 * Custom hook for managing modal states related to form operations.
 */
function useModalControls() {
  const {
    isOpen: isConfirmationModalOpen,
    onOpen: openConfirmationModal,
    onClose: closeConfirmationModal,
  } = useDisclosure();
  const {
    isOpen: isDeleteModalOpen,
    onOpen: openDeleteModal,
    onClose: closeDeleteModal,
  } = useDisclosure();

  return {
    isConfirmationModalOpen,
    openConfirmationModal,
    closeConfirmationModal,
    isDeleteModalOpen,
    openDeleteModal,
    closeDeleteModal,
  };
}

export function useEditForm(params: TEditFormParams) {
  const { t, i18n } = useTranslation();

  const {
    client,
    table_name,
    FormSchema,
    adjustData,
    language,
    fieldsNotApplyTranslation,
    adjustOnReset,
    disable_translations_request = false,
    shouldUnregister = false,
  } = params;

  // Keep UI language aligned only when a language is explicitly provided.
  useEffect(() => {
    if (language && i18n.language !== language) {
      void i18n.changeLanguage(language);
    }
  }, [language, i18n]);

  const navigate = useNavigate();
  const isNew = "isNew" in params;
  const id = "id" in params ? params.id : 0;
  const effectiveLanguage =
    language ?? (i18n.language?.startsWith("en") ? "en" : "fr");

  const [isUpdating, setIsUpdating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [changedFields, setChangedFields] = useState<Record<
    string,
    any
  > | null>(null);
  const [updatedData, setUpdatedData] = useState<Record<string, any> | null>(
    null,
  );

  const {
    isConfirmationModalOpen,
    openConfirmationModal,
    closeConfirmationModal,
    isDeleteModalOpen,
    openDeleteModal,
    closeDeleteModal,
  } = useModalControls();

  const queryClient = useQueryClient();

  const {
    data: fetchedData,
    translations,
    isLoading: isLoadingData,
  } = useFetchItem({
    id,
    client,
    tableName: table_name,
    language: effectiveLanguage,
    schema: FormSchema,
    fieldsNotApplyTranslation,
    isNew,
    disable_translations_request,
  });

  const options = useMemo(() => {
    if (!translations) return {};

    const options = Object.fromEntries(
      Object.entries(translations).map(([category, categoryObj]) => [
        category,
        Object.entries(categoryObj).map(([k, v]) => ({ key: k, value: v })),
      ]),
    ) as Record<
      keyof typeof translations,
      [
        {
          key: string;
          value: string;
        },
      ]
    >;

    return options;
  }, [translations]);

  const { form, handleReset } = useFormState(
    FormSchema,
    fetchedData,
    adjustOnReset,
    translations,
    isNew,
    isLoadingData,
    fieldsNotApplyTranslation,
    shouldUnregister,
    t,
  );

  const updateMutation = useItemUpdate<z.infer<typeof FormSchema>>(
    table_name,
    queryClient,
    client,
    closeConfirmationModal,
    id,
    t,
  );

  const createMutation = useItemCreate<z.infer<typeof FormSchema>>(
    table_name,
    queryClient,
    client,
    closeConfirmationModal,
    t,
  );

  const deleteMutation = useItemDelete(
    table_name,
    queryClient,
    client,
    closeDeleteModal,
    t,
    () => {
      queryClient.invalidateQueries({ queryKey: [client, table_name] });
      queryClient.invalidateQueries({ queryKey: ["audit"] });
      queryClient.invalidateQueries({
        queryKey: [client, "audits", "get", { audit_id: id }],
      });
    },
  );

  const onSubmit = (submittedData: z.infer<typeof FormSchema>) => {
    if (isNew) {
      const { changedFields: newChanges, updatedObject } = createNewItemChanges(
        submittedData,
        adjustData,
        translations,
      );
      setChangedFields(newChanges);
      setUpdatedData(updatedObject);
      openConfirmationModal();
    } else if (fetchedData) {
      const { changedFields, updatedObject } = detectChanges(
        fetchedData,
        submittedData,
        adjustData,
        translations,
      );

      if (Object.keys(changedFields).length === 0) {
        closeConfirmationModal();
      } else {
        setChangedFields(changedFields);
        setUpdatedData(updatedObject);
        openConfirmationModal();
      }
    } else {
      console.error("Original data is not available.");
    }
  };

  const confirmUpdate = () => {
    if (isNew) {
      setIsUpdating(true);
      if (updatedData) {
        createMutation.mutate(updatedData as z.infer<typeof FormSchema>, {
          onSuccess: () => {
            navigate(-1);
          },
          onSettled: () => {
            setIsUpdating(false);
          },
        });
      }
    } else if (changedFields) {
      setIsUpdating(true);
      if (updatedData) {
        updateMutation.mutate(
          { id, changes: updatedData },
          {
            onSuccess: () => {
              form.reset(form.getValues());
            },
            onSettled: () => {
              setIsUpdating(false);
            },
          },
        );
      }
    }
  };

  const handleDelete = () => {
    setIsDeleting(true);
    deleteMutation.mutate(id, {
      onSuccess: () => {
        navigate(-1);
        toast.success(t("form.toast.delete_success"));
      },
      onSettled: () => {
        setIsDeleting(false);
      },
    });
  };

  const cancelUpdate = () => {
    setChangedFields(null);
    closeConfirmationModal();
  };

  return {
    form,
    handleReset,
    onSubmit,
    confirmUpdate,
    cancelUpdate,
    isConfirmationModalOpen,
    isDeleteModalOpen,
    openDeleteModal,
    closeDeleteModal,
    isUpdating,
    isDeleting,
    changedFields,
    fetchedData,
    options,
    handleDelete,
    translations,
    isLoadingData,
  };
}

export function useFormState<FormSchemaType extends FieldValues>(
  FormSchema: z.ZodSchema<FormSchemaType>,
  fetchedData: any,
  adjustOnReset?: (
    key: string,
    value: any,
    fetchedData: any,
    translations?: TTranslation,
  ) => any,
  translations?: TTranslation,
  isNew?: boolean,
  isLoading?: boolean,
  fieldsNotApplyTranslation?: ReadonlyArray<string>,
  shouldUnregister?: boolean,
  t?: (key: string, opts?: any) => string,
) {
  const form = useForm<FormSchemaType>({
    resolver: zodResolver(
      FormSchema as any,
    ) as unknown as Resolver<FormSchemaType>,
    defaultValues: generateDefaultValues(
      FormSchema,
      fetchedData,
    ) as DefaultValues<FormSchemaType>,
    shouldUnregister,
  });

  const fieldsNotApplyTranslationSet = useMemo(
    () => new Set(fieldsNotApplyTranslation ?? []),
    [fieldsNotApplyTranslation],
  );

  useEffect(() => {
    if (isNew) {
      const actualSchema = extractUnderlyingSchema(FormSchema as any);

      if (actualSchema instanceof z.ZodObject) {
        const shape = actualSchema.shape;

        Object.keys(shape).forEach((key) => {
          form.setValue(key as Path<FormSchemaType>, undefined as any);
        });
      }

      return;
    }

    if (!fetchedData || isLoading) return;

    const actualSchema = extractUnderlyingSchema(FormSchema);

    if (actualSchema instanceof z.ZodObject) {
      const shape = actualSchema.shape;

      Object.keys(shape).forEach((key) => {
        let value = fetchedData[key as keyof typeof fetchedData];

        if (
          translations &&
          value &&
          typeof value === "string" &&
          !fieldsNotApplyTranslationSet.has(key)
        ) {
          const fieldTranslations = translations[key];
          if (fieldTranslations) {
            const translatedValue = fieldTranslations[value];
            if (translatedValue) {
              value = translatedValue;
            }
          }
        }

        form.setValue(
          key as Path<FormSchemaType>,
          adjustOnReset
            ? adjustOnReset(key, value, fetchedData, translations)
            : value,
        );
      });
    }
  }, [fetchedData, form, translations, isNew, isLoading]);

  const handleReset = useCallback(() => {
    try {
      if (fetchedData && translations) {
        const actualSchema = extractUnderlyingSchema(FormSchema as any);

        if (actualSchema instanceof z.ZodObject) {
          const shape = actualSchema.shape;
          const resetValues: Partial<FormSchemaType> = {};

          Object.keys(shape).forEach((key) => {
            let value = fetchedData[key as keyof typeof fetchedData];

            if (
              translations &&
              value &&
              typeof value === "string" &&
              !fieldsNotApplyTranslationSet.has(key)
            ) {
              const fieldTranslations = translations[key];
              if (fieldTranslations) {
                const translatedValue = fieldTranslations[value];
                if (translatedValue) {
                  value = translatedValue;
                }
              }
            }

            resetValues[key as keyof FormSchemaType] = adjustOnReset
              ? adjustOnReset(key, value, fetchedData, translations)
              : value;
          });

          form.reset(resetValues as DefaultValues<FormSchemaType>);
          return;
        }
      } else if (translations === undefined) {
        const actualSchema = extractUnderlyingSchema(FormSchema as any);
        const shape =
          actualSchema instanceof z.ZodObject ? actualSchema.shape : {};
        const resetValues: Partial<FormSchemaType> = {};

        Object.keys(shape).forEach((key) => {
          const value = fetchedData[key as keyof typeof fetchedData];
          resetValues[key as keyof FormSchemaType] = adjustOnReset
            ? adjustOnReset(key, value, fetchedData)
            : value;
        });

        form.reset(resetValues as DefaultValues<FormSchemaType>);
        return;
      }

      form.reset(
        generateDefaultValues(
          FormSchema,
          fetchedData,
        ) as DefaultValues<FormSchemaType>,
      );
    } finally {
      const title = t ? t("form.toast.reset_title") : "Modifications annulées";
      const description = t
        ? t("form.toast.reset_description")
        : "Les données ont été réinitialisées à leur état d'origine.";

      toast(title, {
        description,
        duration: 5000,
      });
    }
  }, [fetchedData, form, translations, adjustOnReset, FormSchema, t]);

  return { form, handleReset };
}

export function autoAdjust(data: any, translations?: TTranslation): any {
  if (!data) return data;

  const result = { ...data };

  if (translations) {
    Object.keys(result).forEach((key) => {
      const value = result[key];
      if (value && typeof value === "string" && translations[key]) {
        const translationEntries = Object.entries(translations[key]);
        for (const [originalId, translatedValue] of translationEntries) {
          if (translatedValue === value) {
            result[key] = originalId;
            break;
          }
        }
      }
    });
  }

  Object.keys(result).forEach((key) => {
    const value = result[key];
    const formattedDate = formatDateValueToISOString(value);
    if (formattedDate !== null) {
      result[key] = formattedDate;
    }
  });

  return result;
}

function detectChanges<FormSchemaType>(
  originalData: FormSchemaType,
  submittedData: FormSchemaType,
  adjustData: ((data: any) => any) | undefined,
  translations?: TTranslation,
) {
  let processedOriginal = autoAdjust(originalData, translations);
  let processedSubmitted = autoAdjust(submittedData, translations);

  if (adjustData) {
    processedOriginal = adjustData(processedOriginal);
    processedSubmitted = adjustData(processedSubmitted);
  }

  return getChangedFields(processedOriginal, processedSubmitted);
}

function createNewItemChanges<FormSchemaType extends Record<string, any>>(
  submittedData: FormSchemaType,
  adjustData: ((data: any) => any) | undefined,
  translations?: TTranslation,
) {
  let processedData = { ...submittedData };

  processedData = autoAdjust(processedData, translations);

  if (adjustData) {
    processedData = adjustData(processedData);
  }

  const changedFields = Object.entries(processedData).reduce(
    (acc, [key, value]) => {
      if (value === null || value === undefined || value === "") {
        return acc;
      }

      acc[key] = {
        original: null,
        updated: value,
      };
      return acc;
    },
    {} as Record<string, { original: null; updated: any }>,
  );

  return {
    changedFields,
    updatedObject: processedData,
  };
}
